// utils/api/ImageApi.js
import { apiCall } from './ApiUtils';

/**
 * Image API utilities for document editor
 * Handles image uploads, management, and retrieval
 */
export const imageApi = {
  /**
   * Upload an image file
   * @param {File} imageFile - The image file to upload
   * @param {string} documentId - Optional document ID for organization
   * @param {string} classContext - Class context for API call
   * @param {function} onProgress - Progress callback function
   * @returns {Promise} Upload result with image URL
   */
  uploadImage: async (imageFile, documentId = null, classContext = null, onProgress = null) => {
    const formData = new FormData();
    formData.append('file', imageFile);
    if (documentId) {
      formData.append('document_id', documentId);
    }

    try {
      // Create XMLHttpRequest for progress tracking
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        
        // Progress tracking
        if (onProgress) {
          xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable) {
              const percentComplete = (e.loaded / e.total) * 100;
              onProgress(percentComplete);
            }
          });
        }
        
        // Handle response
        xhr.onload = () => {
          try {
            const response = JSON.parse(xhr.responseText);
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve(response);
            } else {
              reject(new Error(response.error || `HTTP ${xhr.status}`));
            }
          } catch (error) {
            reject(new Error('Failed to parse response'));
          }
        };
        
        xhr.onerror = () => {
          reject(new Error('Network error during upload'));
        };
        
        xhr.ontimeout = () => {
          reject(new Error('Upload timeout'));
        };
        
        // Set up request - using class-based file upload
        xhr.open('POST', `/api/classes/${classContext}/files/upload`);
        xhr.timeout = 30000; // 30 second timeout
        
        // Add authentication headers
        const token = localStorage.getItem('token');
        if (token) {
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        }
        
        // Add class context header if provided
        if (classContext) {
          xhr.setRequestHeader('Class-Code', classContext);
        }
        
        // Send request
        xhr.send(formData);
      });
    } catch (error) {
      console.error('Image upload error:', error);
      throw error;
    }
  },

  /**
   * List uploaded images
   * @param {string} documentId - Optional document ID filter
   * @param {string} classContext - Class context for API call
   * @returns {Promise} List of images
   */
  listImages: (documentId = null, classContext = null) => {
    const params = new URLSearchParams();
    if (documentId) {
      params.append('document_id', documentId);
    }
    
    const url = `/api/classes/${classContext}/files${params.toString() ? `?${params.toString()}` : ''}`;

    return apiCall(url, {
      method: 'GET'
    }, classContext);
  },

  /**
   * Get information about a specific image
   * @param {string} filename - Image filename
   * @param {string} classContext - Class context for API call
   * @returns {Promise} Image information
   */
  getImageInfo: (filename, classContext = null) =>
    apiCall(`/api/classes/${classContext}/files/${encodeURIComponent(filename)}`, {
      method: 'GET'
    }, classContext),

  /**
   * Delete an image
   * @param {string} filename - Image filename to delete
   * @param {string} classContext - Class context for API call
   * @returns {Promise} Deletion result
   */
  deleteImage: (filename, classContext = null) =>
    apiCall(`/api/classes/${classContext}/files/${encodeURIComponent(filename)}`, {
      method: 'DELETE'
    }, classContext),

  /**
   * Check image service health
   * @returns {Promise} Service health status
   */
  healthCheck: (classContext = null) =>
    apiCall(`/api/classes/${classContext}/files/health`, {
      method: 'GET'
    }, classContext),

  /**
   * Validate image file before upload
   * @param {File} file - File to validate
   * @returns {object} Validation result
   */
  validateImageFile: (file) => {
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = [
      'image/png',
      'image/jpeg',
      'image/jpg', 
      'image/gif',
      'image/bmp',
      'image/webp',
      'image/svg+xml',
      'image/x-icon'
    ];
    
    // Check if file exists
    if (!file) {
      return { valid: false, error: 'No file selected' };
    }
    
    // Check file type
    if (!allowedTypes.includes(file.type)) {
      return {
        valid: false,
        error: 'Invalid file type. Supported formats: PNG, JPG, JPEG, GIF, BMP, WebP, SVG, ICO'
      };
    }
    
    // Check file size
    if (file.size > maxSize) {
      return {
        valid: false,
        error: `File too large. Maximum size is ${maxSize / (1024 * 1024)}MB`
      };
    }
    
    // Check if file is empty
    if (file.size === 0) {
      return { valid: false, error: 'Empty file not allowed' };
    }
    
    return { valid: true };
  },

  /**
   * Generate a unique hash for file deduplication checking (client-side)
   * @param {File} file - File to hash
   * @returns {Promise<string>} File hash
   */
  generateFileHash: async (file) => {
    try {
      const buffer = await file.arrayBuffer();
      const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    } catch (error) {
      console.warn('Could not generate file hash:', error);
      return `${file.name}_${file.size}_${file.lastModified}`;
    }
  }
};